import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Loader2, Tag, Plus } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { useAccessibleThemes } from '@/lib/themes/themes-queries';
import {
  useAddExerciseTheme,
  useExerciseThemes,
  useRemoveExerciseTheme,
} from '@/lib/admin/exercise-themes';

interface ExerciseThemesPickerProps {
  exerciseId: string;
}

export function ExerciseThemesPicker({ exerciseId }: ExerciseThemesPickerProps) {
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 250);
    return () => clearTimeout(t);
  }, [input]);

  // Close dropdown on outside click
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const { data: selected, isLoading: loadingSelected } = useExerciseThemes(exerciseId);
  const { data: allThemes, isLoading: loadingThemes } = useAccessibleThemes(
    debounced.length >= 2 ? { search: debounced } : undefined,
  );
  const add = useAddExerciseTheme(exerciseId);
  const remove = useRemoveExerciseTheme(exerciseId);

  const selectedIds = useMemo(() => new Set((selected ?? []).map((s) => s.theme_id)), [selected]);

  const results = useMemo(() => {
    const list = allThemes ?? [];
    // Limit dropdown size + exclude already-selected
    return list.filter((t) => !selectedIds.has(t.id)).slice(0, 8);
  }, [allThemes, selectedIds]);

  const showDropdown = open && (input.trim().length > 0 || results.length > 0);

  return (
    <div className="mb-5">
      <label className="font-display text-ink-700 mb-1.5 block text-[12px] font-semibold">
        Thèmes associés{' '}
        <span className="text-ink-400 font-normal">(recherche puis sélection — facultatif)</span>
      </label>

      <div ref={containerRef} className="relative">
        {/* Selected chips + input — combobox style */}
        <div
          className={cn(
            'border-ink-200 focus-within:ring-primary-400 flex min-h-11 flex-wrap items-center gap-1.5 rounded-xl border bg-white px-2 py-1.5 transition-shadow focus-within:ring-2',
          )}
        >
          {(selected ?? []).map((row) => (
            <span
              key={row.theme_id}
              className="bg-primary-50 text-primary-700 border-primary-100 inline-flex max-w-full items-center gap-1.5 rounded-lg border py-1 pl-2 pr-1 font-sans text-xs"
            >
              <Tag className="h-3 w-3 shrink-0" />
              <span className="truncate">{row.theme.title}</span>
              <button
                type="button"
                onClick={() => remove.mutate(row.theme_id)}
                disabled={remove.isPending && remove.variables === row.theme_id}
                title="Retirer ce thème"
                className="hover:bg-primary-100 text-primary-600 hover:text-primary-800 ml-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md transition-colors disabled:opacity-40"
              >
                {remove.isPending && remove.variables === row.theme_id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <X className="h-3 w-3" />
                )}
              </button>
            </span>
          ))}

          <div className="relative flex min-w-[160px] flex-1 items-center">
            <Search className="text-ink-400 pointer-events-none absolute left-2 h-3.5 w-3.5" />
            <input
              type="text"
              value={input}
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                setInput(e.target.value);
                setOpen(true);
                if (add.error) add.reset();
                if (remove.error) remove.reset();
              }}
              placeholder={
                (selected?.length ?? 0) === 0 ? 'Rechercher un thème…' : 'Ajouter un autre thème…'
              }
              className="text-ink-900 placeholder:text-ink-400 h-8 w-full bg-transparent pl-7 pr-2 font-sans text-sm focus:outline-none"
            />
          </div>
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <div className="border-ink-200 absolute left-0 right-0 top-full z-20 mt-1.5 overflow-hidden rounded-xl border bg-white shadow-lg">
            {debounced.length < 2 ? (
              <p className="text-ink-400 px-4 py-3 font-sans text-xs">
                Saisis au moins 2 caractères pour rechercher.
              </p>
            ) : loadingThemes ? (
              <p className="text-ink-400 inline-flex items-center gap-2 px-4 py-3 font-sans text-xs">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Recherche…
              </p>
            ) : results.length === 0 ? (
              <p className="text-ink-400 px-4 py-3 font-sans text-xs">
                Aucun thème ne correspond à « {debounced} ».
              </p>
            ) : (
              <ul className="max-h-72 overflow-y-auto">
                {results.map((theme, i) => (
                  <li key={theme.id}>
                    <button
                      type="button"
                      onClick={() => {
                        add.mutate(theme.id, {
                          onSuccess: () => {
                            setInput('');
                            setDebounced('');
                          },
                        });
                      }}
                      disabled={add.isPending}
                      className={cn(
                        'hover:bg-ink-50 group flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors',
                        i !== results.length - 1 && 'border-ink-100 border-b',
                      )}
                    >
                      <Tag className="text-ink-400 mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-ink-900 truncate text-sm font-semibold">
                          {theme.title}
                        </p>
                        {theme.short_description && (
                          <p className="text-ink-500 mt-0.5 line-clamp-1 font-sans text-xs">
                            {theme.short_description}
                          </p>
                        )}
                      </div>
                      <span
                        className={cn(
                          'text-ink-400 group-hover:bg-primary-50 group-hover:text-primary-600 mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors',
                        )}
                      >
                        {add.isPending && add.variables === theme.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Plus className="h-3.5 w-3.5" />
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {(add.error || remove.error) && (
        <p className="text-error-600 mt-1.5 font-sans text-xs">
          {add.error?.message ?? remove.error?.message}
        </p>
      )}

      {loadingSelected && (
        <p className="text-ink-400 mt-1.5 inline-flex items-center gap-1.5 font-sans text-xs">
          <Loader2 className="h-3 w-3 animate-spin" /> Chargement des thèmes…
        </p>
      )}
    </div>
  );
}
